import { type ColumnDef } from "@tanstack/react-table";
import { BookOpen, ListTree, Plus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { AppShell } from "@/components/AppShell";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { DataTable } from "@/components/data-table";
import { EmptyState } from "@/components/empty-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { apiErrorMessage } from "@/lib/api";
import {
  useCreateGuideEntry,
  useDeleteGuideEntry,
  useGuideCategories,
  useGuideEntries,
  useUpdateGuideEntry,
} from "@/hooks/api/guides";
import { GuideEntrySheet } from "@/pages/admin/guides/GuideEntrySheet";
import { ManageGuideCategoriesDialog } from "@/pages/admin/guides/ManageGuideCategoriesDialog";
import type { GuideEntry, GuideEntryInput } from "@/types/api";

/**
 * Admin CRUD for Guide entries — reference content (FAQs, difficult-caller
 * playbooks, anything not captured by a branching Script) that Staff can
 * look up on demand. No draft/publish step: saving an entry makes it
 * visible to Staff immediately, see apps/guides/views.py.
 */
export default function AdminGuidesPage() {
  const { data, isLoading, error } = useGuideEntries();
  const { data: categoriesData } = useGuideCategories();
  const createEntry = useCreateGuideEntry();
  const updateEntry = useUpdateGuideEntry();
  const deleteEntry = useDeleteGuideEntry();

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<GuideEntry | null>(null);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<GuideEntry | null>(null);

  const categories = categoriesData?.results ?? [];

  const openCreate = () => {
    setEditingEntry(null);
    setSheetOpen(true);
  };
  const openEdit = (entry: GuideEntry) => {
    setEditingEntry(entry);
    setSheetOpen(true);
  };

  const handleSubmit = async (input: GuideEntryInput) => {
    try {
      if (editingEntry) {
        await updateEntry.mutateAsync({ id: editingEntry.id, input });
        toast.success("Guide entry updated.");
      } else {
        await createEntry.mutateAsync(input);
        toast.success("Guide entry created.");
      }
      setSheetOpen(false);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't save that entry."));
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteEntry.mutateAsync(pendingDelete.id);
      toast.success("Guide entry deleted.");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't delete that entry."));
    } finally {
      setPendingDelete(null);
    }
  };

  const columns: ColumnDef<GuideEntry>[] = [
    { accessorKey: "title", header: "Title" },
    {
      accessorKey: "category",
      header: "Category",
      cell: ({ row }) => row.original.category.name,
    },
    {
      accessorKey: "entry_type",
      header: "Type",
      cell: ({ row }) => (
        <Badge tone={row.original.entry_type === "REBUTTAL" ? "warning" : "neutral"}>
          {row.original.entry_type === "REBUTTAL" ? "Rebuttal" : "Guideline"}
        </Badge>
      ),
    },
    {
      id: "hasSpiel",
      header: "Has spiel",
      cell: ({ row }) => (row.original.spiel_text.trim() ? "Yes" : "—"),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <div className="flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={() => openEdit(row.original)}>
            Edit
          </Button>
          <Button variant="danger" size="sm" onClick={() => setPendingDelete(row.original)}>
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <AppShell>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="m-0 text-2xl font-bold text-slate-900">Guides</h1>
          <p className="mt-1 text-sm text-slate-600">
            FAQs, difficult-caller playbooks, and other reference content that doesn't fit a
            step-by-step Script. Visible to Staff as soon as you save it.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setCategoriesOpen(true)}>
            <ListTree className="h-4 w-4" aria-hidden="true" />
            Manage categories
          </Button>
          <Button onClick={openCreate} disabled={categories.length === 0}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            New entry
          </Button>
        </div>
      </div>

      {categories.length === 0 && (
        <p className="mt-4 text-sm text-slate-500">
          Create a category first — use "Manage categories" above.
        </p>
      )}

      <div className="mt-6">
        <DataTable
          columns={columns}
          data={data?.results ?? []}
          loading={isLoading}
          error={error ? "Couldn't load guide entries." : undefined}
          empty={
            <EmptyState
              icon={BookOpen}
              title="No guide entries yet"
              description="Add reference content for situations your Scripts don't cover — FAQs, difficult-caller guidance, anything a CSR needs to look up mid-call."
              action={
                <Button onClick={openCreate} disabled={categories.length === 0}>
                  New entry
                </Button>
              }
            />
          }
        />
      </div>

      <GuideEntrySheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        entry={editingEntry}
        categories={categories}
        onSubmit={handleSubmit}
        saving={createEntry.isPending || updateEntry.isPending}
      />

      <ManageGuideCategoriesDialog
        open={categoriesOpen}
        onClose={() => setCategoriesOpen(false)}
      />

      <ConfirmDialog
        isOpen={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        variant="danger"
        title="Delete this guide entry?"
        message={
          pendingDelete
            ? `"${pendingDelete.title}" will no longer be visible to Staff. This can't be undone.`
            : ""
        }
        confirmText="Delete"
        isLoading={deleteEntry.isPending}
      />
    </AppShell>
  );
}
